import {ComboboxProvider, SelectGroup, SelectProvider} from '@ariakit/react'
import {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectGroupLabel,
    SelectItem,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {medicalReportKeyMapToLabel} from '@/features/medical-reports/utils'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {useGetMedicalReportTypes} from '@/features/medical-reports/queries/useGetMedicalReportTypes'
import {MedicalReportType, MedicalReportTypeKeyName} from '@/types'

export const InputSelectReportType = ({
    label,
    placeholder,
    value,
    disabled,
    errorMessage,
    specializationId,
    disabledTypes,
    onChange,
    onClear
}: {
    label?: string
    value?: MedicalReportType | null
    placeholder?: string
    disabled?: boolean
    errorMessage?: string
    specializationId?: string
    /** Report types shown but not selectable, e.g. project-bound types when there is no project */
    disabledTypes?: readonly MedicalReportTypeKeyName[]
    onChange: (value: MedicalReportType | null) => void
    onClear?: () => void
}) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputRef = useRef(null)
    const medicalReportTypesQuery = useGetMedicalReportTypes({
        specializationId,
        options: {enabled: isQueryEnabled}
    })
    const medicalReports = medicalReportTypesQuery.data || []
    const hiddenTypes: MedicalReportTypeKeyName[] = ['renew_close_rehab_project']

    return (
        <ComboboxProvider setValue={medicalReportTypesQuery.onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setValue={value =>
                    onChange(medicalReports.flatMap(group => group.types).find(type => type.id == value) || null)
                }
                setOpen={isOpen => {
                    setIsQueryEnabled(isOpen)
                    medicalReportTypesQuery.onResetSearch(inputRef)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    {label && <Label>{label}</Label>}
                    <SelectTrigger
                        disabled={disabled}
                        placeholder={placeholder || t('commons:select')}
                        fullWidth
                        value={value?.id}
                        hasError={!!errorMessage}
                        displayValue={() => (
                            <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                {value?.keyName && t(medicalReportKeyMapToLabel[value?.keyName])}
                                {onClear && (
                                    <XCloseIcon
                                        role="button"
                                        onClick={e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            onClear()
                                        }}
                                    />
                                )}
                            </Flexbox>
                        )}
                    />
                    {errorMessage && (
                        <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}
                    <SelectComboboxPopover sameWidth>
                        <SelectComboboxInput ref={inputRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {medicalReportTypesQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : medicalReportTypesQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : medicalReports.length > 0 ? (
                                medicalReports.map(group => (
                                    <SelectGroup key={group.groupKeyName}>
                                        <SelectGroupLabel>{group.groupName}</SelectGroupLabel>
                                        {group.types.map(option => (
                                            <SelectItem
                                                key={option.id}
                                                value={option.id}
                                                disabled={disabledTypes?.includes(option.keyName)}
                                                style={hiddenTypes.includes(option.keyName) ? {display: 'none'} : {}}
                                                hasItemCheckbox
                                            >
                                                {t(medicalReportKeyMapToLabel[option.keyName])}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                ))
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
